import { CheckIcon } from "@phosphor-icons/react/dist/ssr";
import type { ComponentPropsWithRef } from "react";

import { cn } from "@/core/shared/v2/lib/utils";

export interface StepsProps extends ComponentPropsWithRef<"nav"> {
  /** Step labels, in order. */
  steps: string[];
  /** 0-based index of the active step. */
  currentStep: number;
  /** Compact mobile summary, e.g. "Krok 2 z 4". Falls back to "current/total". */
  progressLabel?: string;
}

/**
 * Responsive progress indicator (no DaisyUI — shadcn-style, see button/badge).
 * Desktop (lg+): full horizontal stepper with numbers/checkmarks, connectors and labels.
 * Mobile (<lg): compact "Krok X z Y" + progress bar + current step name — avoids the
 * label-overflow that a horizontal stepper hits on narrow screens.
 */
export function Steps({ steps, currentStep, progressLabel, className, ...props }: StepsProps) {
  if (steps.length === 0 || currentStep < 0 || currentStep >= steps.length) return null;

  const total = steps.length;
  const summary = progressLabel ?? `${currentStep + 1}/${total}`;

  return (
    <nav data-slot="steps" aria-label={summary} className={cn("w-full", className)} {...props}>
      {/* Desktop: centered, constrained horizontal stepper. The active step is filled + haloed so
          "you are here" (and that payment is the final step) is unmissable. */}
      <ol className="mx-auto hidden w-full max-w-2xl items-start lg:flex">
        {steps.map((label, i) => {
          const isCompleted = i < currentStep;
          const isActive = i === currentStep;

          return (
            <li key={label} className="flex flex-1 flex-col items-center gap-3">
              <div className="flex w-full items-center">
                <span
                  aria-hidden
                  className={cn(
                    "h-1 flex-1 rounded-full",
                    i === 0 && "opacity-0",
                    i <= currentStep ? "bg-primary" : "bg-border",
                  )}
                />
                <span
                  aria-current={isActive ? "step" : undefined}
                  className={cn(
                    "flex size-10 shrink-0 items-center justify-center rounded-full border-2 font-bold text-base transition-all",
                    isCompleted && "border-primary bg-primary text-primary-foreground",
                    isActive && "scale-110 border-primary bg-primary text-primary-foreground ring-4 ring-primary/20",
                    !isCompleted && !isActive && "border-border bg-background text-muted-foreground",
                  )}
                >
                  {isCompleted ? <CheckIcon weight="bold" className="size-5" /> : i + 1}
                </span>
                <span
                  aria-hidden
                  className={cn(
                    "h-1 flex-1 rounded-full",
                    i === total - 1 && "opacity-0",
                    i < currentStep ? "bg-primary" : "bg-border",
                  )}
                />
              </div>
              <span
                className={cn(
                  "max-w-[9rem] text-center text-sm leading-tight",
                  isActive && "font-bold text-foreground",
                  isCompleted && "font-medium text-foreground",
                  !isActive && !isCompleted && "text-muted-foreground",
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>

      {/* Mobile: compact summary + progress bar + current step name */}
      <div className="lg:hidden">
        <p className="font-semibold text-foreground text-sm">{summary}</p>
        <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${((currentStep + 1) / total) * 100}%` }}
          />
        </div>
        <p className="mt-2 font-bold text-base text-foreground">{steps[currentStep]}</p>
      </div>
    </nav>
  );
}
